import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Button, 
  Tabs, 
  Tab,
  Alert,
  CircularProgress
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import PDFEditorComponent from './PDFEditorComponent';
import OCRPDFEditor from './OCRPDFEditor';
import FileUploadComponent from './FileUploadComponent';

interface DocumentPageProps {
  // Add props as needed
}

const DocumentPage: React.FC<DocumentPageProps> = () => {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  
  const [document, setDocument] = useState<any | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editorMode, setEditorMode] = useState<'annotation' | 'ocr'>('ocr');
  
  // Fetch document if documentId is provided
  useEffect(() => {
    if (documentId) {
      fetchDocument(documentId);
    }
  }, [documentId]);
  
  // Fetch document data
  const fetchDocument = async (id: string) => {
    setLoading(true);
    setError(null);
    
    try {
      // Mock API call
      setTimeout(() => {
        setDocument({
          id,
          title: `Document ${id}`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
        setLoading(false);
      }, 1000);
    } catch (err) {
      console.error('Error fetching document:', err);
      setError('Failed to fetch document');
      setLoading(false);
    }
  };
  
  // Handle file upload
  const handleFileUpload = (file: File) => {
    console.log('File uploaded:', file);
    setFile(file);
  };
  
  // Handle document save
  const handleSaveDocument = async (data: any) => {
    console.log('Saving document:', data);
    
    // Mock API call
    return new Promise<void>((resolve, reject) => {
      setTimeout(() => {
        // Simulate successful save
        console.log('Document saved successfully');
        resolve();
      }, 1000);
    });
  };
  
  // Handle AI request
  const handleAIRequest = async (prompt: string, context: string) => {
    console.log('AI request:', prompt, context);
    
    // Mock AI response
    return new Promise<string>((resolve) => {
      setTimeout(() => {
        resolve(`AI response to: "${prompt}"\n\nHere is a suggested revision based on your request...`);
      }, 2000);
    });
  };
  
  // Handle editor mode change
  const handleEditorModeChange = (event: React.SyntheticEvent, newValue: 'annotation' | 'ocr') => {
    setEditorMode(newValue);
  };
  
  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {document ? document.title : 'Document Editor'}
          </Typography>
          
          <Button 
            variant="outlined" 
            onClick={() => navigate('/documents')}
          >
            Back to Documents
          </Button>
        </Box>
        
        {/* Loading state */}
        {loading && (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        )}
        
        {/* Error message */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {/* File upload if no file is selected */}
        {!file && !loading && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Upload Document
            </Typography>
            <FileUploadComponent onFileUpload={handleFileUpload} />
          </Paper>
        )}
        
        {/* Editor mode selector */}
        {file && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs 
              value={editorMode} 
              onChange={handleEditorModeChange}
              aria-label="editor mode tabs"
            >
              <Tab label="OCR Text Editor" value="ocr" />
              <Tab label="Annotation Mode" value="annotation" />
            </Tabs>
          </Box>
        )}
        
        {/* Document editor */}
        {file && editorMode === 'ocr' && (
          <OCRPDFEditor 
            file={file}
            documentId={documentId}
            onSave={handleSaveDocument}
          />
        )}
        
        {file && editorMode === 'annotation' && (
          <PDFEditorComponent 
            file={file}
            documentId={documentId}
            onSave={handleSaveDocument}
            onAIRequest={handleAIRequest}
          />
        )}
      </Box>
    </Container>
  );
};

export default DocumentPage; 